import {
  deleteAPI,
  getAPI,
  postAPI,
  postBlobAPI,
  putAPI,
  patchAPI,
  uploadCSVAPI,
} from "./Networking";
import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { GetPacksParams, GetShipmentsParams } from "../stores/ShipmentStore";
import { BulkUpdate, ShipmentsDetails, Swagpack } from "../models/Shipments";
import { ShipmentRequest } from "../models/AddShipment";
import dayjs from "dayjs";

export default class ShipmentsAPI {
  getQueryParams = (params: any): URLSearchParams => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page_no", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.sortBy) queryParams.append("sort_by", params.sortBy);
    if (params.sortType) queryParams.append("sort_type", params.sortType);
    if (params.search) queryParams.append("search_text", params.search);
    if (params.allSelected === true) queryParams.append("all_selected", "true");
    if (params.tab_id) queryParams.append("tab_id", params.tab_id);

    if (params.status && params.status.length > 0) {
      queryParams.append("status", params.status.join(","));
    }
    if (params.delivery_partner_id && params.delivery_partner_id.length > 0) {
      queryParams.append(
        "delivery_partner_id",
        params.delivery_partner_id.join(","),
      );
    }

    if (params.pack_ids && params.pack_ids.length > 0) {
      queryParams.append(
        "pack_ids",
        params.pack_ids.map((pack: any) => pack.id).join(","),
      );
    }

    if (
      params.inventory_type &&
      (params.inventory_type === "1" || params.inventory_type === "2")
    ) {
      queryParams.append("inventory_type", params.inventory_type);
    }

    if (params.shipment_type && params.shipment_type.length > 0) {
      queryParams.append("shipment_type", params.shipment_type.join(","));
    }

    if (params.c_id && params.c_id.length > 0) {
      queryParams.append("c_id", params.c_id[0].id);
    }

    if (params.order_id && params.order_id.length > 0) {
      queryParams.append("order_id", params.order_id.join(","));
    }

    if (params.contact_number && params.contact_number !== "") {
      queryParams.append("contact_number", params.contact_number);
    }

    const DimensionsFilter = [
      { id: 1, value: "dimensions" },
      { id: 2, value: "weight" },
      { id: 3, value: "shipping_cost" },
      { id: 4, value: "awb" },
      { id: 5, value: "packs" },
    ];

    if (params.dimensions && params.dimensions.length > 0) {
      DimensionsFilter.forEach((filter) => {
        if (params.dimensions?.includes(filter.id)) {
          queryParams.append(filter.value, "true");
        }
      });
    }

    if (params.packs_exist !== undefined) {
      queryParams.append("packs_exist", params.packs_exist ? "true" : "false");
    }

    if (params.country && params.country.length > 0) {
      queryParams.append("country", params.country.join(","));
    }

    if (params.search_text_value && params.search_text_value.length > 0) {
      queryParams.append(
        "search_text_value",
        params.search_text_value.join(","),
      );
    }

    if (params.start_date && params.start_date !== "") {
      const start_date = dayjs(params.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      queryParams.append("start_date", start_date);
    }

    if (params.end_date && params.end_date !== "") {
      const end_date = dayjs(params.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      queryParams.append("end_date", end_date);
    }

    return queryParams;
  };

  getShipments = async (
    params: GetShipmentsParams,
    download_csv: boolean = false,
    signal?: AbortSignal,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      if (download_csv) {
        const response = await postBlobAPI(
          `/v5/shipments?${queryParams.toString()}`,
          { download_csv: true, emails: params.emails },
        );
        const blob = new Blob([response], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Master_ShipmentReport.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const response = await postAPI(
          `/v5/shipments?${queryParams.toString()}`,
          { download_csv: false, emails: params.emails },
          signal,
        );
        return response.result as PaginatedAPIResponse<ShipmentsDetails>;
      }
    } catch (error) {
      throw error;
    }
  };

  editShipment = async (
    shipmentId: number,
    shipmentDetails: ShipmentsDetails,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/shipments/shipmentId/${shipmentId}`, {
        shipmentDetails: shipmentDetails,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  deleteShipment = async (
    shipmentId: number,
    status: number,
    reason?: string,
  ): Promise<APIResponse> => {
    try {
      const response = await deleteAPI(
        `/v5/shipments/shipmentId/${shipmentId}/status/${status}`,
        reason ? { reason } : undefined,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  removePacksFromShipment = async (
    shipmentId: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/shipments/remove/pack/shipmentId/${shipmentId}`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  moveShipmentToNew = async (shipmentId: number): Promise<APIResponse> => {
    try {
      const response = await patchAPI(
        `/v5/shipments/shipmentId/${shipmentId}/moveToNew`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getDeliveryPartner = async (
    searchText?: string,
    is_logistics_vendor: boolean = false,
    isPrinter: boolean = false,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/delivery?limit=50&search_text=${searchText}`,
        {
          is_logistics_vendor: is_logistics_vendor,
          is_printer: isPrinter,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  bulkShipmentUpdate = async (
    payload: BulkUpdate,
    params: GetShipmentsParams,
  ): Promise<APIResponse> => {
    try {
      const {
        shipment_ids,
        length,
        breadth,
        height,
        weight,
        status,
        delivery_partner_id,
        shipment_method,
      } = payload;

      const queryParams = this.getQueryParams(params);

      const response = await postAPI(
        `/v5/shipments/bulkUpdate?${queryParams.toString()}`,
        {
          shipment_ids,
          length,
          breadth,
          height,
          weight,
          status,
          delivery_partner_id,
          shipment_method,
        },
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadCsvForBulkEdit = async (file: File | null): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }

      const response = await uploadCSVAPI(
        `/v5/shipments/csv_bulk_update`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  processShipments = async (shipmentIds: number[]): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/shipments/process`, {
        shipment_ids: shipmentIds,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  getOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/shipments/order-id/${orderId}`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadShiprocketCSV = async (params: GetShipmentsParams): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await postBlobAPI(
        `/v5/shipments/shiprocket_template?${queryParams.toString()}`,
        { emails: params.emails },
      );
      const blob = new Blob([response], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const now = new Date().toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
      });
      const formattedDate = now.replace(/\//g, "-").replace(",", "_");
      const fileName = `shiprocket_template_${formattedDate}`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  };

  downloadBulkShipmentsCSVReport = async (
    params: GetShipmentsParams,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await getAPI(
        `/v5/shipments/download/bulk?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShiprocketCSVBulkReport = async (
    params: GetShipmentsParams,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await getAPI(
        `/v5/shipments/shiprocket_template/bulk?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPacksByInventoryId = async (
    inventory_id: number,
    search?: string,
  ): Promise<APIResponse> => {
    try {
      const params = new URLSearchParams();
      if (search && search.trim()) {
        params.set("search_text", search.trim());
      }
      const queryString = params.toString();
      const url = `/v5/shipments/packs/${inventory_id}/inventory_id${
        queryString ? `?${queryString}` : ""
      }`;
      const response = await getAPI(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShipmentPacks = async (params: GetPacksParams): Promise<APIResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.search_text && params.search_text.trim()) {
        queryParams.set("search_text", params.search_text.trim());
      }
      if (params.available_only) {
        queryParams.set("available_only", "true");
      }
      if (params.consumed_only) {
        queryParams.set("consumed_only", "true");
      }
      const response = await postAPI(
        `/v5/shipments/packs/shipment_ids?${queryParams.toString()}`,
        {
          shipment_ids: params.shipment_ids,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  savePacks = async (
    shipment_ids: number[],
    shipment_packs: Swagpack,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/shipments/add_packs`, {
        shipment_ids,
        shipment_packs,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  searchPacks = async (search_text: string): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/shipments/search-packs?search_text=${search_text}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createShipment = async (shipment: ShipmentRequest): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/shipments/shipmentRequest`, {
        shipment,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  createShipmentThroughCsv = async (
    orderId: number,
    file: File | null,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const response = await uploadCSVAPI(
        `/v5/shipments/csvShipmentRequest/${orderId}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createBulkShipmentsViaInventoryIdAndCsv = async (
    inventoryId: number,
    file: File | null,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const response = await uploadCSVAPI(
        `/v5/shipments/csvShipmentRequest/inventory-id/${inventoryId}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getCountByStatus = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/shipments/count-by-status`);
      return response;
    } catch (error) {
      throw error;
    }
  };
}
